import { useEffect } from 'react';
/**
 * Keeps the map usable when the device is rotated (portrait <-> landscape).
 *
 * - Maintains an `--app-height` CSS custom property equal to the real viewport
 *   height, so the map frame does not jump as mobile browser chrome collapses.
 * - Debounces a Leaflet `invalidateSize()` after the viewport settles, because
 *   some mobile browsers fire `resize`/`orientationchange` before the new
 *   dimensions are final (which can leave a mis-sized canvas or grey tiles).
 *
 * No sensor APIs are used: the browser already turns a physical rotation into a
 * `resize`/`orientationchange` event.
 */
export function useOrientationResize() {
    useEffect(() => {
        const root = document.documentElement;
        const setAppHeight = () => {
            root.style.setProperty('--app-height', `${window.innerHeight}px`);
        };
        let timer;
        const onViewportChange = () => {
            setAppHeight();
            window.clearTimeout(timer);
            timer = window.setTimeout(() => {
                const map = window.map;
                map?.invalidateSize?.();
            }, 200);
        };
        setAppHeight(); // prime on mount
        window.addEventListener('resize', onViewportChange);
        window.addEventListener('orientationchange', onViewportChange);
        return () => {
            window.clearTimeout(timer);
            window.removeEventListener('resize', onViewportChange);
            window.removeEventListener('orientationchange', onViewportChange);
        };
    }, []);
}
